import { ILoggingService, IMessagingService, FormComponent, ValidationSummaryComponent, ISessionService } from "@se/designsystem-core";
import { ServiceResponseNoContent, IApiClient, IApiClientId, ServiceResponseErrorCode } from "@se/platform/common";
import { CreateEmailEnquiryDto } from "../models/CreateEmailEnquiryDto";
import { inject } from "inversify";
import { first } from "rxjs/operators";
import { fromEvent, combineLatest } from "rxjs";
class EmailEnquiryFormComponent extends FormComponent {

    private formId: string;

    private formEl: HTMLElement
    private errorMessageEl: HTMLElement;
    private successMessageEl: HTMLElement;

    private firstnameEl: HTMLElement;
    private lastnameEl: HTMLElement;
    private emailAddressEl: HTMLElement;
    private messageEl: HTMLElement;
    private customerReferenceEl: HTMLElement;
    private privacyEl: HTMLElement;
    private submitBtn: HTMLButtonElement;

    private validationSummaryEl: HTMLElement;
    private validationSummary: ValidationSummaryComponent;

    constructor(componentEl: HTMLElement,
        protected appInsights: ILoggingService,
        protected messagingService: IMessagingService,
        private sessionService: ISessionService,
        @inject(IApiClientId) private apiClient: IApiClient) {

        super(componentEl, messagingService, null, appInsights);

        this.formId = componentEl.dataset["formId"];

        this.formEl = componentEl.querySelector<HTMLElement>(".js-form")
        this.errorMessageEl = componentEl.querySelector<HTMLElement>(".js-errorMessage")
        this.successMessageEl = componentEl.querySelector<HTMLElement>(".js-successMessage")

        this.firstnameEl = componentEl.querySelector<HTMLElement>(".js-firstname")
        this.lastnameEl = componentEl.querySelector<HTMLElement>(".js-lastname")
        this.messageEl = componentEl.querySelector<HTMLElement>(".js-message")
        this.privacyEl = componentEl.querySelector<HTMLElement>(".js-privacy")
        this.emailAddressEl = componentEl.querySelector<HTMLElement>(".js-emailAddress")
        this.customerReferenceEl = componentEl.querySelector<HTMLElement>(".js-customerReference")
        this.submitBtn = componentEl.querySelector<HTMLButtonElement>(".js-submitBtn")

        this.validationSummaryEl = document.querySelector<HTMLElement>(".js-validation-summary");
        this.validationSummary = new ValidationSummaryComponent(this.validationSummaryEl, this.messagingService, this);

        // wait for the session to start before hooking up the button event (cant submit without an access token)
        const onSessionStarted$ = this.sessionService.OnSessionStarted$().pipe(first());
        const onSubmitClicked$ = fromEvent(this.submitBtn, 'click');
        const onSubmit$ = combineLatest([onSessionStarted$, onSubmitClicked$]);
        
        onSubmit$.subscribe(async ([session, _]) => {
            await this.submitEnquiry(session.accessToken);
        });

        this.subscribeToInputChanges(this.firstnameEl);
        this.subscribeToInputChanges(this.lastnameEl);
        this.subscribeToInputChanges(this.emailAddressEl);
        this.subscribeToInputChanges(this.messageEl);
        this.subscribeToInputChanges(this.privacyEl);
    }

    private subscribeToInputChanges(fieldEl: HTMLElement) {

        const onFieldChanged$ = fromEvent<InputEvent>(fieldEl, 'input');
        onFieldChanged$.subscribe(f => {

            const field = f.target as HTMLElement;
            field.closest(".form-field").classList.remove("form-field--invalid");
            this.validationSummary.hideError(field.dataset.fieldId);
        });

    }

    private async submitEnquiry(accessToken: string) {

        // disable button and reset error states
        this.submitBtn.disabled = true;
        this.errorMessageEl.classList.add("hidden");
        this.validationSummary.clearErrors();

        let enquiryDto = new CreateEmailEnquiryDto();
        enquiryDto.firstname = this.firstnameEl.querySelector<HTMLInputElement>(".js-value").value;
        enquiryDto.lastname = this.lastnameEl.querySelector<HTMLInputElement>(".js-value").value;
        enquiryDto.emailAddress = this.emailAddressEl.querySelector<HTMLInputElement>(".js-value").value;
        enquiryDto.customerReference = this.customerReferenceEl.querySelector<HTMLInputElement>(".js-value").value;
        enquiryDto.message = this.messageEl.querySelector<HTMLTextAreaElement>(".js-value").value;
        enquiryDto.privacy = this.privacyEl.querySelector<HTMLInputElement>(".js-value").checked;

        const response = await this.apiClient.PostNoResponse<CreateEmailEnquiryDto>(location.origin, `api/enquiries/${this.formId}`, enquiryDto, accessToken);

        if (response.wasSuccessful) {
            // show success message, hide form
            this.successMessageEl.classList.remove("hidden");
            this.formEl.classList.add("hidden");
        }
        else if (response.errorCode === ServiceResponseErrorCode.ValidationError) {
            // show validation summary
            response.validationErrors.forEach(e => {

                // find form field, set error message and state
                const fieldEl = this.containerEl.querySelector<HTMLElement>(`.js-${e.fieldName}`)

                if (fieldEl) {
                    const fieldErrorEl = fieldEl.querySelector<HTMLElement>(".js-error-message");

                    if (fieldErrorEl) {
                        fieldErrorEl.innerHTML = e.errorMessage;
                    }

                    fieldEl.classList.add("form-field--invalid");
                }
            });

            this.validationSummary.showErrors(response.validationErrors);
            this.validationSummary.focusErrors();
        }
        else {
            // show error message
            this.errorMessageEl.classList.remove("hidden");
            this.validationSummary.show();
        }

        this.submitBtn.disabled = false;
    }
}

export { EmailEnquiryFormComponent };
